import React, { useState, useRef, useMemo } from "react";
import axios from "axios";
import "./InternalReports.css";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LinearProgress, Typography, Box, Button, CircularProgress, Tabs, Tab } from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExFacSummaries from "./Components/ExFac/ExFacSummaries";
import EndOfMonthSummary from "./EndOfMonthSummary";

function InternalReport() {
    const [selectedDate, setSelectedDate] = useState(null);
    const [salesData, setSalesData] = useState([]);
    const [includedRows, setIncludedRows] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [totalSalesValue, setTotalSalesValue] = useState(0);
    const [monthlyBudget, setMonthlyBudget] = useState(0);
    const [workingDaysSoFar, setWorkingDaysSoFar] = useState(0);
    const [workingDaysRemaining, setWorkingDaysRemaining] = useState(0);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [generatingExcel, setGeneratingExcel] = useState(false);
    const [showEndOfMonth, setShowEndOfMonth] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const apiUrl = process.env.REACT_APP_API_URL;
    const backgroundGradient = 'linear-gradient(to right, #F0F4F7, #FFFFFF)';

    const exfacRef = useRef(null);

    const fetchSalesData = async (date) => {
        setLoading(true);
        setMessage("");

        try {
            const formattedDate = dayjs(date).format("YYYY-MM-DD");
            const response = await axios.get(`${apiUrl}/api/get_sales_by_date`, {
                params: { date: formattedDate },
                withCredentials: true
            });

            if (response.status === 200 && response.data.sales.length > 0) {
                const sales = response.data.sales.map((sale, index) => ({
                    ...sale,
                    included: true,
                }));

                setSalesData(sales);
                setIncludedRows(sales.reduce((acc, sale, index) => {
                    acc[index] = true;
                    return acc;
                }, {}));
            } else {
                setSalesData([]);
                setIncludedRows({});
                setMessage("No sales data found for the selected date.");
            }
            setTotalSalesValue(response.data.total_sales_value);
            setMonthlyBudget(response.data.monthly_budget);
            setWorkingDaysSoFar(response.data.working_days_so_far);
            setWorkingDaysRemaining(response.data.working_days_remaining);
            setShowEndOfMonth(response.data.last_day_and_today_after);
        } catch (error) {
            console.error("Error fetching sales data:", error);
            setMessage("Error fetching sales data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const toggleRow = (index) => {
        setIncludedRows((prev) => {
            const updatedRows = { ...prev, [index]: !prev[index] };
            recalculateTotalSales(updatedRows);
            return updatedRows;
        });
    };

    const recalculateTotalSales = (updatedRows) => {
        const newTotal = salesData
            .filter((_, index) => updatedRows[index])
            .reduce((sum, sale) => sum + sale.total_value, 0);
        setTotalSalesValue(newTotal);
    };

    const formatCurrencyNumber = (value) => new Intl.NumberFormat('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    const formatNumber = (value) => new Intl.NumberFormat('en-GB').format(value);

    const budgetFulfillment = monthlyBudget > 0 ? (totalSalesValue / monthlyBudget) * 100 : 0;

    const exportToExcel = async () => {
        // If selectedDate is bad, bail out *before* setting loading
        const dateObj = new Date(selectedDate);
        if (isNaN(dateObj.getTime())) {
            console.error("Invalid selectedDate:", selectedDate);
            return;
        }

        setGeneratingExcel(true);

        try {
            const dateParam = dateObj.toISOString().slice(0, 10);

            const response = await axios.get(`${apiUrl}/api/get_sales_by_year`, {
            params: { date: dateParam },
            responseType: "blob",
            withCredentials: true,
            });

            const blob = new Blob([response.data], {
            type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", `sales_${dateObj.getFullYear()}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Clean up the object URL
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading Excel:", err);
        } finally {
            setGeneratingExcel(false);
        }
    };

    // **🔹 Function to Export PDF**
    const exportToPDF = async () => {
        setGeneratingPDF(true);
        const doc = new jsPDF({ unit: 'mm', format: 'a4' });
        let y = 15;
        const M = 14;
        // --- Header -------------------------------------------------------
        doc.setFontSize(12);
        doc.text(dayjs(selectedDate).format("MMMM YYYY"), M, y); 
        y += 10;

        doc.setFontSize(11);
        doc.text("Date: " + dayjs(selectedDate).format("DD.MM.YY"), M, y);
        y += 8;

        doc.setFontSize(9);
        doc.text("Monthly Budget: £" + formatCurrencyNumber(monthlyBudget), M, y); 
        y += 5;
        doc.text("Ex-Fac Total: £" + formatCurrencyNumber(totalSalesValue), M, y); 
        y += 6;

        // --- Progress bar (% of monthly budget) --------------------------
        (function drawProgressBar() {
            const label = "% Monthly Budget";
            const value = Number(budgetFulfillment || 0);
            const cappedValue = Math.min(Math.max(value, 0), 100);

            const x = M;
            const barYTop = y + 2;
            const barWidth = 100;
            const barHeight = 3;
            const radius = barHeight / 2;
            const filledWidth = (cappedValue / 100) * barWidth;

            doc.setFontSize(9);
            doc.text(label, x, y);

            // background
            doc.setFillColor(161, 172, 182);
            doc.roundedRect(x, barYTop, barWidth, barHeight, radius, radius, 'F');

            // fill
            doc.setFillColor(10, 36, 64);
            doc.roundedRect(x, barYTop, filledWidth, barHeight, radius, radius, 'F');

            // value
            doc.text(value.toFixed(2) + "%", x + barWidth + 4, barYTop + barHeight + 1);
            y = barYTop + barHeight + 6;
        })();

        // --- Working days chips ------------------------------------------
        doc.text("Working Days Completed: " + (workingDaysSoFar || 0), M, y); 
        y += 5;
        doc.text("Working Days Remaining: " + (workingDaysRemaining || 0), M, y); 
        y += 6;

        // --- Main table (unchanged layout) -------------------------------
        autoTable(doc, {
            startY: y,
            head: [["Product", "Mid-Month £", "Mid-Month Units", "Budget for Month - Units", "Stock at Movianto - Units"]],
            body: salesData
            .filter(function(_, i) { return includedRows[i]; })
            .map(function(s) {
                return [
                s.brand,
                "£" + formatCurrencyNumber(s.total_value),
                formatNumber(s.total_quantity),
                formatNumber(s.stock_quantity),
                formatNumber(calculateFactoryStock(s.stock_factory_quantity, s.total_quantity)),
                ];
            }),
            headStyles: { fillColor: [10, 36, 64] }
        });

        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const contentW = pageW - M * 2;

        // Read finalY robustly across autotable versions
        const tableFinalY =
            (doc.lastAutoTable && doc.lastAutoTable.finalY) ||
            (doc.autoTable && doc.autoTable.previous && doc.autoTable.previous.finalY) ||
            (autoTable.previous && autoTable.previous.finalY) ||
            y;

        // Move cursor below table with padding
        y = tableFinalY + 8;

        // If we're too close to the bottom, start a fresh page
        if (y > pageH - M - 10) {
            doc.addPage();
            y = M;
        }

        // --- Pull export assets from child (charts + stats) ---------------
        let exfac = null;
        if (exfacRef.current && typeof exfacRef.current.getExportPayload === 'function') {
            try {
            exfac = await exfacRef.current.getExportPayload();
            } catch (e) {
            // If the child isn't ready, continue with just the first page
            exfac = null;
            }
        }

        if (exfac) {
            // Section title
            if (y + 10 > pageH - M) { doc.addPage(); y = M; }
            doc.setFontSize(12);
            doc.text("Ex-Fac Summaries", M, y);
            y += 2;

            if (exfac.chartInsightsPng) {
                y = await addImageSmart(doc, exfac.chartInsightsPng, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            else{
                y += 8;
            }

            // 1) Main chart
            if (exfac.chartMainPng) {
                y = addPageHeading(doc, "Monthly £ Total Portfolio", M, y);
                y = await addImageSmart(doc, exfac.chartMainPng, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            
            // 2) YTD vs EOY
            if (exfac.chartYTDPng) {
                y = forceNewPage(doc, M);
                y = addPageHeading(doc, "YTD £ Total Portfolio", M, y);
                y = await addImageSmart(doc, exfac.chartYTDPng, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }

            // 3) Expected Orders
            if (exfac.chartExpectedPng) {
                y = await addImageSmart(doc, exfac.chartExpectedPng, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }

            if (exfac.c250MonthlyCashChart) {
                y = forceNewPage(doc, M);
                y = addPageHeading(doc, "Cholurso 250mg End of Month", M, y);
                
                y = await addImageSmart(doc, exfac.c250MonthlyCashChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.c250MonthlyUnitsChart) {
                y = await addImageSmart(doc, exfac.c250MonthlyUnitsChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.c250YearlyCashChart) {
                y = forceNewPage(doc, M);
                y = addPageHeading(doc, "Cholurso 250mg Year to Date", M, y);
                
                y = await addImageSmart(doc, exfac.c250YearlyCashChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.c250YearlyUnitsChart) {
                y = await addImageSmart(doc, exfac.c250YearlyUnitsChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }

            if (exfac.c500MonthlyCashChart) {
                y = forceNewPage(doc, M);
                y = addPageHeading(doc, "Cholurso 500mg End of Month", M, y);

                y = await addImageSmart(doc, exfac.c500MonthlyCashChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.c500MonthlyUnitsChart) {
                y = await addImageSmart(doc, exfac.c500MonthlyUnitsChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.c500YearlyCashChart) {
                y = forceNewPage(doc, M);
                y = addPageHeading(doc, "Cholurso 500mg Year to Date", M, y);

                y = await addImageSmart(doc, exfac.c500YearlyCashChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.c500YearlyUnitsChart) {
                y = await addImageSmart(doc, exfac.c500YearlyUnitsChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }

            if (exfac.simalviaMonthlyCashChart) {
                y = forceNewPage(doc, M);
                y = addPageHeading(doc, "Simalvia End of Month", M, y);

                y = await addImageSmart(doc, exfac.simalviaMonthlyCashChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.simalviaMonthlyUnitsChart) {
                y = await addImageSmart(doc, exfac.simalviaMonthlyUnitsChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.simalviaYearlyCashChart) {
                y = forceNewPage(doc, M);
                y = addPageHeading(doc, "Simalvia Year to Date", M, y);

                y = await addImageSmart(doc, exfac.simalviaYearlyCashChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.simalviaYearlyUnitsChart) {
                y = await addImageSmart(doc, exfac.simalviaYearlyUnitsChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }

            if (exfac.transisoftMonthlyCashChart) {
                y = forceNewPage(doc, M);
                y = addPageHeading(doc, "Transisoft End of Month", M, y);

                y = await addImageSmart(doc, exfac.transisoftMonthlyCashChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.transisoftMonthlyUnitsChart) {
                y = await addImageSmart(doc, exfac.transisoftMonthlyUnitsChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.transisoftYearlyCashChart) {
                y = forceNewPage(doc, M);
                y = addPageHeading(doc, "Transisoft Year to Date", M, y);

                y = await addImageSmart(doc, exfac.transisoftYearlyCashChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
            if (exfac.transisoftYearlyUnitsChart) {
                y = await addImageSmart(doc, exfac.transisoftYearlyUnitsChart, {
                    xMm: M, yMm: y, maxWidthMm: contentW, marginMm: M,
                    format: 'JPEG', quality: 0.82
                });
                y += 6;
            }
        }
        // --- Save ---------------------------------------------------------
        setGeneratingPDF(false);
        if (selectedTab === 0){
            doc.save("Daily_Sales_Report_" + dayjs(selectedDate).format("YYYY-MM-DD") + ".pdf");
        } else {
            doc.save("End_of_Month_Report_" + dayjs(selectedDate).format("MMMM YYYY") + ".pdf");
        }
    };

    const calculateFactoryStock = (factoryStock, quantity) => {
        if (factoryStock === 0)
            return 0;

        return factoryStock - quantity
    };
    
    async function addImagePaginated(doc, dataUrl, {
        xMm = 14,
        yMm = 14,
        maxWidthMm,
        marginMm = 14,
        format = 'JPEG',
        quality = 0.82
        } = {}) {
        if (!dataUrl) return yMm;

        // load image to know pixel size
        const img = await new Promise((resolve, reject) => {
            const im = new Image();
            im.onload = () => resolve(im);
            im.onerror = reject;
            im.src = dataUrl;
        });

        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const usableW = maxWidthMm || (pageW - xMm - marginMm);
        const targetWmm = Math.min(usableW, pageW - xMm - marginMm);

        const pxPerMm = img.naturalWidth / targetWmm;

        let remainingPx = img.naturalHeight;
        let srcYpx = 0;
        let curYmm = yMm;

        const scratch = document.createElement('canvas');
        scratch.width = img.naturalWidth;

        while (remainingPx > 0) {
            let availHmm = pageH - curYmm - marginMm;
            if (availHmm <= 0) {
                doc.addPage();
                curYmm = marginMm;
                availHmm = pageH - curYmm - marginMm;
            }

            const slicePx = Math.min(remainingPx, Math.floor(availHmm * pxPerMm));
            if (slicePx <= 0) break;

            scratch.height = slicePx;
            const sctx = scratch.getContext('2d');
            sctx.fillStyle = '#ffffff';
            sctx.fillRect(0, 0, scratch.width, scratch.height);
            sctx.drawImage(
                img,
                0, srcYpx,
                img.naturalWidth, slicePx,
                0, 0,
                scratch.width, slicePx
            );

            const sliceUrl = scratch.toDataURL(
                format === 'PNG' ? 'image/png' : 'image/jpeg',
                format === 'PNG' ? undefined : quality
            );

            const sliceHmm = slicePx / pxPerMm;
            doc.addImage(sliceUrl, format, xMm, curYmm, targetWmm, sliceHmm, undefined, 'FAST');

            curYmm += sliceHmm;
            srcYpx += slicePx;
            remainingPx -= slicePx;

            if (remainingPx > 0) {
                doc.addPage();
                curYmm = marginMm;
            }
        }

        return curYmm; // next Y to continue on the last page used
    }

    // Convert any dataURL to JPEG (smaller) at given quality
    async function dataUrlToJpeg(dataUrl, quality = 0.82, bg = '#ffffff') {
        const img = await new Promise((resolve, reject) => {
            const im = new Image();
            im.onload = () => resolve(im);
            im.onerror = reject;
            im.src = dataUrl;
        });
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        return canvas.toDataURL('image/jpeg', quality);
    }

    // Place an image whole when possible; else slice across pages.
    // Returns the next Y position (mm) on the last page used.
    async function addImageSmart(doc, dataUrl, {
    xMm = 14,
    yMm = 14,
    maxWidthMm,
    marginMm = 14,
    format = 'JPEG',
    quality = 0.82,
    background = '#ffffff'
    } = {}) {
        if (!dataUrl) return yMm;

        // Load once to measure
        const img = await new Promise((resolve, reject) => {
            const im = new Image();
            im.onload = () => resolve(im);
            im.onerror = reject;
            im.src = dataUrl;
        });

        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const usableW = maxWidthMm || (pageW - xMm - marginMm);
        const targetWmm = Math.min(usableW, pageW - xMm - marginMm);

        // Compute proportional height at target width
        const aspect = img.naturalHeight / img.naturalWidth;
        const targetHmm = targetWmm * aspect;

        // Remaining space on current page
        const remainingHmm = pageH - yMm - marginMm;

        // If it fits fully on the current page, drop it in one go
        if (targetHmm <= remainingHmm) {
            const src = (format === 'JPEG')
            ? await dataUrlToJpeg(dataUrl, quality, background)
            : dataUrl;
            doc.addImage(src, format, xMm, yMm, targetWmm, targetHmm, undefined, 'FAST');
            return yMm + targetHmm;
        }

        // If it fits fully on a fresh page, break first, then drop whole
        if (targetHmm <= (pageH - 2 * marginMm)) {
            doc.addPage();
            const yTop = marginMm;
            const src = (format === 'JPEG')
            ? await dataUrlToJpeg(dataUrl, quality, background)
            : dataUrl;
            doc.addImage(src, format, xMm, yTop, targetWmm, targetHmm, undefined, 'FAST');
            return yTop + targetHmm;
        }

        // Otherwise, slice across pages
        return await addImagePaginated(doc, dataUrl, {
            xMm, yMm, maxWidthMm: targetWmm, marginMm, format, quality
        });
    }

    function forceNewPage(doc, margin = 14) {
        doc.addPage();
        return margin;   // reset y
    }

    function addPageHeading(doc, heading, margin, yPos) {
        doc.text(heading, margin, yPos);
        return yPos + 5;
    }

    const handleTabChange = (_, newIndex) => setSelectedTab(newIndex);

    const renderExportButton = () => (
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 1, marginBottom: '10px'}}
          onClick={exportToPDF}
          disabled={generatingPDF || generatingExcel}
        >
          {generatingPDF ? <CircularProgress size={20} /> : 'Export to PDF'}
        </Button>
    );

    const renderExportExcel = () => (
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 1, marginBottom: '10px'}}
          onClick={exportToExcel}
          disabled={generatingPDF || generatingExcel}
        >
          {generatingExcel ? <CircularProgress size={20} /> : 'Export to Excel'}
        </Button>
    );

    const canShowEndOfMonthTab = (selectedDate) => {
        if (!selectedDate) return false;

        const d = dayjs(selectedDate);
        if (!d.isValid()) return false;

        // Work out the last working day of that month
        let lastWorking = d.endOf("month"); // last calendar day
        while (lastWorking.day() === 0 || lastWorking.day() === 6) {
            // 0 = Sun, 6 = Sat
            lastWorking = lastWorking.subtract(1, "day");
        }

        // Only consider dates in the SAME month/year
        const sameMonth = d.month() === lastWorking.month() && d.year() === lastWorking.year();
        if (!sameMonth) return false;

        // Show tab if selectedDate is ON or AFTER the last working day
        return d.isSame(lastWorking, "day") || d.isAfter(lastWorking, "day");
    };

    return (
        <>
            <Box sx={{ padding: 3, background: backgroundGradient, minHeight: '100vh' }}>
                <div className="sales-report-container">
                    <h2>Daily Sales Report</h2>
                    <div className="date-picker-container">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Select a Date"
                                value={selectedDate}
                                onChange={(date) => {
                                    setSelectedDate(date);
                                    fetchSalesData(date);
                                }}
                                disabled={selectedTab === 1}
                                format="DD/MM/YYYY"
                                maxDate={dayjs()} 
                                renderInput={(params) => <input {...params} />}
                            />
                        </LocalizationProvider>
                    </div>

                    {selectedDate && (
                        <div className="summary-section">
                            <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                                <Tab label="Overview" />
                                {showEndOfMonth && (
                                    <Tab label="End of Month Summary" />
                                )}
                            </Tabs>
                            <h3>{dayjs(selectedDate).format("MMMM YYYY")}</h3>
                            <p><strong>Date:</strong> {dayjs(selectedDate).format("DD.MM.YY")}</p>
                            <p><strong>Monthly Budget:</strong> £{formatCurrencyNumber(monthlyBudget)}</p>
                            <p><strong>Ex-Fac Total:</strong> £{formatCurrencyNumber(totalSalesValue)}</p>
                            <Typography variant="p">% Monthly Budget</Typography>
                            <Box display="flex" alignItems="center">
                                <Box sx={{ width: "80%", mr: 1 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={budgetFulfillment > 100 ? 100 : budgetFulfillment } 
                                        sx={{ height: 10, borderRadius: 5 }}
                                    />
                                </Box>
                                <Typography variant="body1">
                                    {budgetFulfillment.toFixed(2)}%
                                </Typography>
                            </Box>
                            <p><strong>Working Days Completed:</strong> {workingDaysSoFar}</p>
                            <p><strong>Working Days Remaining:</strong> {workingDaysRemaining}</p>
                            <div style={{ display: "flex", gap: "12px" }}>
                                { renderExportButton() }
                                { renderExportExcel() }
                            </div>
                            {message && <p className="message">{message}</p>}
                            {salesData.length > 0 && (
                                <div className="table-responsive">
                                    <table className="sales-table">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Product</th>
                                                <th>Mid-Month £</th>
                                                <th>Mid-Month Units</th>
                                                <th>Budget for Month - Units</th>
                                                <th>Stock at Movianto - Units</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {salesData.map((sale, index) => (
                                                <tr key={index} className={includedRows[index] ? "" : "excluded-row"}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={includedRows[index] || false}
                                                            onChange={() => toggleRow(index)}
                                                        />
                                                    </td>
                                                    <td>{sale.brand}</td>
                                                    <td>£{formatCurrencyNumber(sale.total_value)}</td>
                                                    <td>{formatNumber(sale.total_quantity)}</td>
                                                    <td>{formatNumber(sale.stock_quantity)}</td>
                                                    <td>{formatNumber(calculateFactoryStock(sale.stock_factory_quantity, sale.total_quantity))}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {selectedTab === 0 && (
                                <ExFacSummaries ref={exfacRef} apiUrl={apiUrl} showAskRxAI={false} selectedDate={dayjs(selectedDate).format("YYYY-MM-DD")} />
                            )}

                            {selectedTab === 1 && (
                                <EndOfMonthSummary ref={exfacRef} apiUrl={apiUrl} selectedDate={dayjs(selectedDate).format("YYYY-MM-DD")}/>
                            )}
                        </div>
                    )}

                    {loading && <p>Loading sales data...</p>}                
                </div>
            </Box>
        </>
    );
}

export default InternalReport;
